import type { ColorPaletteConfiguration } from '@/ui/color-picker/ui-color-picker.component';
import { uniqueFilter } from '@/utils/toolkit/array/unique-filter.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { Injectable, type Type } from '@angular/core';
import { BusinessIntelligenceBarChartComponent } from '../../components/business-intelligence-data-representations/business-intelligence-bar-chart/business-intelligence-bar-chart';
import { BusinessIntelligenceHistogramComponent } from '../../components/business-intelligence-data-representations/business-intelligence-histogram/business-intelligence-histogram';
import { BusinessIntelligenceLinesChartComponent } from '../../components/business-intelligence-data-representations/business-intelligence-lines-chart/business-intelligence-lines-chart';
import { BusinessIntelligencePieChartComponent } from '../../components/business-intelligence-data-representations/business-intelligence-pie-chart/business-intelligence-pie-chart';
import { BusinessIntelligencePivotTableComponent } from '../../components/business-intelligence-data-representations/business-intelligence-pivot-table/business-intelligence-pivot-table';
import type { BusinessIntelligenceRepresentationComponent } from '../../components/business-intelligence-data-representations/business-intelligence-representation-component.class';
import { BusinessIntelligenceScatterChartComponent } from '../../components/business-intelligence-data-representations/business-intelligence-scatter-chart/business-intelligence-scatter-chart';
import { BusinessIntelligenceSingleValueComponent } from '../../components/business-intelligence-data-representations/business-intelligence-single-value/business-intelligence-single-value';
import { BusinessIntelligenceTableComponent } from '../../components/business-intelligence-data-representations/business-intelligence-table/business-intelligence-table';
import { BusinessIntelligenceBarChartRepresentationConfigEditorComponent } from '../../components/tailored-widget-editor/business-intelligence-representation-config-editors/business-intelligence-bar-chart-representation-config-editor/business-intelligence-bar-chart-representation-config-editor';
import { BusinessIntelligenceHistogramRepresentationConfigEditorComponent } from '../../components/tailored-widget-editor/business-intelligence-representation-config-editors/business-intelligence-histogram-representation-config-editor/business-intelligence-histogram-representation-config-editor';
import { BusinessIntelligenceLineChartRepresentationConfigEditorComponent } from '../../components/tailored-widget-editor/business-intelligence-representation-config-editors/business-intelligence-line-chart-representation-config-editor/business-intelligence-line-chart-representation-config-editor';
import { BusinessIntelligencePieChartRepresentationConfigEditorComponent } from '../../components/tailored-widget-editor/business-intelligence-representation-config-editors/business-intelligence-pie-chart-representation-config-editor/business-intelligence-pie-chart-representation-config-editor';
import { BusinessIntelligencePivotTableRepresentationConfigEditorComponent } from '../../components/tailored-widget-editor/business-intelligence-representation-config-editors/business-intelligence-pivot-table-representation-config-editor/business-intelligence-pivot-table-representation-config-editor';
import type { BusinessIntelligenceRepresentationConfigEditorComponent } from '../../components/tailored-widget-editor/business-intelligence-representation-config-editors/business-intelligence-representation-config-editor.class';
import { BusinessIntelligenceScatterChartRepresentationConfigEditorComponent } from '../../components/tailored-widget-editor/business-intelligence-representation-config-editors/business-intelligence-scatter-chart-representation-config-editor/business-intelligence-scatter-chart-representation-config-editor';
import { BusinessIntelligenceSingleValueRepresentationConfigEditorComponent } from '../../components/tailored-widget-editor/business-intelligence-representation-config-editors/business-intelligence-single-value-representation-config-editor/business-intelligence-single-value-representation-config-editor';
import { BusinessIntelligenceTableRepresentationConfigEditorComponent } from '../../components/tailored-widget-editor/business-intelligence-representation-config-editors/business-intelligence-table-representation-config-editor/business-intelligence-table-representation-config-editor';
import { SortOrder } from '../../data-viz/data-source.interface';
import {
    type BusinessIntelligenceDimension,
    type BusinessIntelligenceMeasure,
    type BusinessIntelligenceMeasureCode,
    BusinessIntelligenceMemberType,
    type BusinessIntelligenceSortCriterion
} from '../../interfaces/BusinessIntelligence.interface';
import {
    BusinessIntelligenceComboChartMeasureChartType,
    BusinessIntelligenceRepresentationCode,
    type BusinessIntelligenceRepresentationCodeComponentConfig,
    type BusinessIntelligenceRepresentationCodeConfig,
    isBusinessIntelligenceDimension,
    isBusinessIntelligenceMeasure
} from '../../interfaces/BusinessIntelligenceRepresentation.interface';
import { continuousFormattableType, FormattableType, temporalFormattableType } from '../../interfaces/Formatter.interface';
import type {
    BusinessIntelligenceDimensionConfiguration,
    BusinessIntelligenceMeasureConfiguration
} from '../../interfaces/TailoredWidget.interface';
import { isContinuousDimension } from './business-intelligence.utils';

interface RepresentationDescription<Code extends BusinessIntelligenceRepresentationCode> {
    representationCode: Code;

    configEditorComponent: Type<
        BusinessIntelligenceRepresentationConfigEditorComponent<BusinessIntelligenceRepresentationCodeConfig<Code>>
    >;
    isRepresentationConfigValid: (config: BusinessIntelligenceRepresentationCodeConfig<Code>, metadata: MembersMetaData) => boolean;

    getMeasuresFromRepresentationConfig: (
        config: BusinessIntelligenceRepresentationCodeConfig<Code>
    ) => BusinessIntelligenceMeasureConfiguration[];
    getDimensionsFromRepresentationConfig: (
        config: BusinessIntelligenceRepresentationCodeConfig<Code>
    ) => BusinessIntelligenceDimensionConfiguration[];
    getSortFromRepresentationConfig: (
        config: BusinessIntelligenceRepresentationCodeConfig<Code>,
        metadata: MembersMetaData
    ) => BusinessIntelligenceSortCriterion[];
    getLimitFromRepresentationConfig: (config: BusinessIntelligenceRepresentationCodeConfig<Code>) => number | null;
    getShouldFilterNullResultFromRepresentationConfig: (config: BusinessIntelligenceRepresentationCodeConfig<Code>) => boolean | null;

    representationConfigToCommonConfig: (representationConfig: BusinessIntelligenceRepresentationCodeConfig<Code>) => CommonConfig;
    commonConfigToRepresentationConfig: (
        commonConfig: CommonConfig,
        metadata: MembersMetaData
    ) => BusinessIntelligenceRepresentationCodeConfig<Code>;

    representationComponent: Type<BusinessIntelligenceRepresentationComponent<BusinessIntelligenceRepresentationCodeComponentConfig<Code>>>;
    representationConfigToComponentConfig: (
        representationConfig: BusinessIntelligenceRepresentationCodeConfig<Code>
    ) => BusinessIntelligenceRepresentationCodeComponentConfig<Code>;

    defaultConfig: BusinessIntelligenceRepresentationCodeConfig<Code>;
}

interface CommonConfig {
    measures: BusinessIntelligenceMeasureConfiguration[];
    dimensions: BusinessIntelligenceDimensionConfiguration[];
    sortCriteria: BusinessIntelligenceSortCriterion[];
    limit: number | null;
}

export interface MembersMetaData {
    measures: BusinessIntelligenceMeasure[];
    dimensions: BusinessIntelligenceDimension[];
}

const representationDescriptions: { [Code in BusinessIntelligenceRepresentationCode]: RepresentationDescription<Code> } = {
    [BusinessIntelligenceRepresentationCode.BAR_CHART]: {
        representationCode: BusinessIntelligenceRepresentationCode.BAR_CHART,

        configEditorComponent: BusinessIntelligenceBarChartRepresentationConfigEditorComponent,
        isRepresentationConfigValid: (config, metadata) => {
            if (!config) {
                return false;
            }
            if (!(config.measure && config.measure.measureCode)) {
                return false;
            }
            if (!(config.categorization && config.categorization.dimensionCode)) {
                return false;
            }

            const categorizationDimension = metadata.dimensions.find(
                dimension => dimension.dimensionCode === config.categorization!.dimensionCode
            );

            const categorizationIsNotTemporal: boolean = !temporalFormattableType.includes(categorizationDimension?.typeConfig.type!);

            return categorizationIsNotTemporal;
        },

        getMeasuresFromRepresentationConfig: config => (config.measure ? [{ measureCode: config.measure.measureCode }] : []),
        getDimensionsFromRepresentationConfig: config =>
            config.segmentation ? [config.categorization!, config.segmentation] : [config.categorization!],
        getSortFromRepresentationConfig: (config, metaData) => {
            // If specific sort is registered
            if (config.sortCriteria?.length > 0) return config.sortCriteria;

            const xAxisDimension = metaData.dimensions.find(dimension => dimension.dimensionCode === config.categorization!.dimensionCode);
            if (xAxisDimension && isContinuousDimension(xAxisDimension)) {
                return [
                    {
                        memberCode: config.categorization!.dimensionCode,
                        sortDirection: SortOrder.ASC
                    } as BusinessIntelligenceSortCriterion
                ];
            } else {
                return [
                    {
                        memberCode: config.measure.measureCode,
                        sortDirection: SortOrder.DESC
                    } as BusinessIntelligenceSortCriterion
                ];
            }
        },
        getLimitFromRepresentationConfig: config => config.limit ?? null,
        getShouldFilterNullResultFromRepresentationConfig: () => null,

        representationConfigToCommonConfig: config =>
            config
                ? {
                      measures: config.measure && config.measure.measureCode ? [config.measure] : [],
                      dimensions: [config.categorization, config.segmentation].filter(isNotNullish),
                      sortCriteria: config.sortCriteria,
                      limit: config.limit ?? null
                  }
                : {
                      measures: [],
                      dimensions: [],
                      sortCriteria: [],
                      limit: null
                  },
        commonConfigToRepresentationConfig: commonConfig => ({
            categorization: commonConfig.dimensions[0],
            segmentation: commonConfig.dimensions[1],
            measure: commonConfig.measures.length ? { measureCode: commonConfig.measures[0].measureCode } : null!,
            sortCriteria: commonConfig.sortCriteria,
            limit: commonConfig.limit ?? undefined,
            shouldZeroBeDisplayed: false
        }),
        representationComponent: BusinessIntelligenceBarChartComponent,
        representationConfigToComponentConfig: representationConfig => ({
            measureCode: representationConfig.measure.measureCode ?? null,
            categorizationDimensionCode: representationConfig.categorization!.dimensionCode,
            segmentationDimensionCode: representationConfig.segmentation ? representationConfig.segmentation.dimensionCode : null!,
            areSeriesFormattedForColor: !representationConfig.limit,
            keepZeros: !!representationConfig.limit || (representationConfig.shouldZeroBeDisplayed ?? false)
        }),
        defaultConfig: {
            measure: { measureCode: null! },
            categorization: null,
            segmentation: null,
            sortCriteria: [],
            limit: undefined,
            shouldZeroBeDisplayed: false
        }
    },
    [BusinessIntelligenceRepresentationCode.HISTOGRAM]: {
        representationCode: BusinessIntelligenceRepresentationCode.HISTOGRAM,

        configEditorComponent: BusinessIntelligenceHistogramRepresentationConfigEditorComponent,
        isRepresentationConfigValid: (config, metadata) => {
            if (!config) {
                return false;
            }
            if (!(config.measure && config.measure.measureCode)) {
                return false;
            }
            if (!(config.xAxis && config.xAxis.dimensionCode)) {
                return false;
            }

            const xAxisDimension = metadata.dimensions.find(dimension => dimension.dimensionCode === config.xAxis.dimensionCode);

            const xAxisIsNotTemporalOrHasValidParams: boolean =
                !temporalFormattableType.includes(xAxisDimension?.typeConfig.type!) ||
                (isNotNullish(config.xAxis.groupingParams) && isNotNullish(config.xAxis.groupingParams.granularity));

            return xAxisIsNotTemporalOrHasValidParams;
        },

        getMeasuresFromRepresentationConfig: config => (config.measure ? [{ measureCode: config.measure.measureCode }] : []),
        getDimensionsFromRepresentationConfig: config => {
            const dimensions = [config.xAxis];

            if (config.segmentation) {
                dimensions.push(config.segmentation);
            }

            if (config.click) {
                dimensions.push(config.click);
            }

            return dimensions;
        },
        getSortFromRepresentationConfig: (config, metaData) => {
            // If specific sort is registered
            if (config.sortCriteria?.length > 0) return config.sortCriteria;
            const xAxisDimension = metaData.dimensions.find(dimension => dimension.dimensionCode === config.xAxis.dimensionCode);
            const sortCriteria: BusinessIntelligenceSortCriterion[] = [];
            if (xAxisDimension && isContinuousDimension(xAxisDimension)) {
                sortCriteria.push({
                    memberCode: config.xAxis.dimensionCode,
                    sortDirection: SortOrder.ASC
                });
            } else {
                sortCriteria.push({
                    memberCode: config.measure.measureCode,
                    sortDirection: SortOrder.DESC
                });
            }
            if (config.segmentation) sortCriteria.push({ memberCode: config.segmentation.dimensionCode, sortDirection: SortOrder.DESC });
            return sortCriteria;
        },

        getLimitFromRepresentationConfig: config => config.limit ?? null,
        getShouldFilterNullResultFromRepresentationConfig: () => null,

        representationConfigToCommonConfig: config =>
            config
                ? {
                      measures: config.measure && config.measure.measureCode ? [config.measure] : [],
                      dimensions: [config.xAxis, config.segmentation, config.click].filter(isNotNullish),
                      sortCriteria: config.sortCriteria,
                      limit: config.limit ?? null,
                      shouldZeroBeDisplayed: config.shouldZeroBeDisplayed
                  }
                : {
                      measures: [],
                      dimensions: [],
                      sortCriteria: [],
                      limit: null,
                      shouldZeroBeDisplayed: false
                  },
        commonConfigToRepresentationConfig: commonConfig => ({
            xAxis: commonConfig.dimensions[0],
            segmentation: commonConfig.dimensions[1],
            click: null,
            measure: commonConfig.measures.length ? { measureCode: commonConfig.measures[0].measureCode } : null!,
            sortCriteria: commonConfig.sortCriteria,
            limit: commonConfig.limit ?? null,
            shouldZeroBeDisplayed: false
        }),
        representationComponent: BusinessIntelligenceHistogramComponent,
        representationConfigToComponentConfig: representationConfig => ({
            measureCode: representationConfig.measure ? representationConfig.measure.measureCode : null!,
            xAxisDimensionCode: representationConfig.xAxis.dimensionCode,
            clickDimensionCode: representationConfig.click?.dimensionCode ?? null,
            segmentationDimensionCode: representationConfig.segmentation ? representationConfig.segmentation.dimensionCode : null,
            areSeriesFormattedForColor: !representationConfig.limit,
            keepZeros: !!representationConfig.limit || (representationConfig.shouldZeroBeDisplayed ?? false)
        }),
        defaultConfig: {
            measure: { measureCode: null! },
            xAxis: null!,
            click: null,
            segmentation: null,
            sortCriteria: [],
            limit: null,
            shouldZeroBeDisplayed: false
        }
    },
    [BusinessIntelligenceRepresentationCode.PIE_CHART]: {
        representationCode: BusinessIntelligenceRepresentationCode.PIE_CHART,

        configEditorComponent: BusinessIntelligencePieChartRepresentationConfigEditorComponent,
        isRepresentationConfigValid: config => {
            return Boolean(config && config.measure && config.measure.measureCode);
        },

        getMeasuresFromRepresentationConfig: config => [{ measureCode: config.measure.measureCode }],
        getDimensionsFromRepresentationConfig: config => (config.segmentation ? [config.segmentation] : []),
        getSortFromRepresentationConfig: () => [],
        getLimitFromRepresentationConfig: () => null,
        getShouldFilterNullResultFromRepresentationConfig: () => null,

        representationConfigToCommonConfig: config =>
            config
                ? {
                      measures: config.measure.measureCode ? [{ measureCode: config.measure.measureCode }] : [],
                      dimensions: [config.segmentation].filter(isNotNullish),
                      sortCriteria: [],
                      limit: null
                  }
                : {
                      measures: [],
                      dimensions: [],
                      sortCriteria: [],
                      limit: null
                  },
        commonConfigToRepresentationConfig: (commonConfig, metaData) => {
            const segmentation = commonConfig.dimensions.find(dimensionConfiguration => {
                const dimension = metaData.dimensions.find(dimension => dimension.dimensionCode === dimensionConfiguration.dimensionCode);
                if (!dimension) return;

                return !continuousFormattableType.includes(dimension.typeConfig.type);
            });
            return {
                measure:
                    commonConfig.measures && commonConfig.measures.length ? { measureCode: commonConfig.measures[0].measureCode } : null!,
                segmentation: segmentation!,
                areDataLabelsActivated: false
            };
        },

        representationComponent: BusinessIntelligencePieChartComponent,
        representationConfigToComponentConfig: representationConfig => ({
            measureCode: representationConfig.measure.measureCode,
            segmentationDimensionCode: representationConfig.segmentation ? representationConfig.segmentation.dimensionCode : null!,
            areDataLabelsActivated: representationConfig.areDataLabelsActivated!
        }),
        defaultConfig: {
            measure: { measureCode: null! },
            segmentation: null,
            areDataLabelsActivated: false
        }
    },
    [BusinessIntelligenceRepresentationCode.LINE_CHART]: {
        representationCode: BusinessIntelligenceRepresentationCode.LINE_CHART,

        configEditorComponent: BusinessIntelligenceLineChartRepresentationConfigEditorComponent,
        isRepresentationConfigValid: (config, metadata) => {
            if (!config) {
                return false;
            }
            if (!(config.xAxis && config.xAxis.dimensionCode)) {
                return false;
            }

            const xAxisDimension = metadata.dimensions.find(dimension => dimension.dimensionCode === config.xAxis.dimensionCode)!;

            if (
                temporalFormattableType.includes(xAxisDimension.typeConfig.type) &&
                !(config.xAxis.groupingParams && config.xAxis.groupingParams.granularity)
            ) {
                return false;
            }

            if (!(config.measures && config.measures.length > 0)) {
                return false;
            }

            if (!config.measures.every(measure => !!measure.measureCode)) {
                return false;
            }

            const configMeasureCodes = config.measures.map(measure => measure.measureCode);
            const firstMeasure = metadata.measures.find(measure => measure.measureCode === configMeasureCodes[0])!;

            for (const configMeasureCode of configMeasureCodes) {
                const currentMeasure = metadata.measures.find(measure => measure.measureCode === configMeasureCode)!;
                if (currentMeasure.typeConfig.type !== firstMeasure.typeConfig.type) {
                    return false;
                }
            }

            if (firstMeasure.typeConfig.type === FormattableType.VALUE_WITH_UNIT) {
                const firstUnitCode = firstMeasure.typeConfig.typeParams.unitCode;
                for (const controlMeasureCode of configMeasureCodes) {
                    const currentMeasure = metadata.measures.find(measure => measure.measureCode === controlMeasureCode)!;
                    if (
                        (!(currentMeasure.typeConfig.type === FormattableType.VALUE_WITH_UNIT) ||
                            currentMeasure.typeConfig.typeParams.unitCode) !== firstUnitCode
                    ) {
                        return false;
                    }
                }
            }

            return true;
        },

        getMeasuresFromRepresentationConfig: config =>
            config.measures
                .map(measure => {
                    return {
                        measureCode: measure.measureCode
                    };
                })
                .filter(uniqueFilter),
        getDimensionsFromRepresentationConfig: config => (config.segmentation ? [config.xAxis, config.segmentation] : [config.xAxis]),
        getSortFromRepresentationConfig: (config, metaData) => {
            const xAxisDimension = metaData.dimensions.find(dimension => dimension.dimensionCode === config.xAxis.dimensionCode);
            const sortCriteria: BusinessIntelligenceSortCriterion[] = [];
            if (xAxisDimension && isContinuousDimension(xAxisDimension)) {
                sortCriteria.push({
                    memberCode: config.xAxis.dimensionCode,
                    sortDirection: SortOrder.ASC
                });
            } else {
                sortCriteria.push({
                    memberCode: config.measures[0].measureCode,
                    sortDirection: SortOrder.DESC
                });
            }
            if (config.segmentation) sortCriteria.push({ memberCode: config.segmentation.dimensionCode, sortDirection: SortOrder.DESC });
            return sortCriteria;
        },
        getLimitFromRepresentationConfig: () => null,
        getShouldFilterNullResultFromRepresentationConfig: () => null,

        representationConfigToCommonConfig: config =>
            config
                ? {
                      measures: config.measures
                          .map(measure => ({ measureCode: measure.measureCode }))
                          .filter(isNotNullish)
                          .filter(uniqueFilter),
                      dimensions: [config.xAxis, config.segmentation].filter(isNotNullish),
                      sortCriteria: [],
                      limit: null
                  }
                : {
                      measures: [],
                      dimensions: [],
                      sortCriteria: [],
                      limit: null
                  },
        commonConfigToRepresentationConfig: commonConfig => ({
            xAxis: commonConfig.dimensions[0],
            segmentation: commonConfig.dimensions[1],
            measures: commonConfig.measures.map(measure => {
                return {
                    measureCode: measure.measureCode,
                    isCumulative: false,
                    chartType: BusinessIntelligenceComboChartMeasureChartType.LINE,
                    columnStackingGroup: null,
                    color: measure.color ?? null
                };
            }),
            displayZeros: null
        }),

        representationComponent: BusinessIntelligenceLinesChartComponent,
        representationConfigToComponentConfig: representationConfig => ({
            measures: representationConfig.measures as {
                measureCode: BusinessIntelligenceMeasureCode;
                isCumulative: boolean;
                chartType: BusinessIntelligenceComboChartMeasureChartType;
                columnStackingGroup: number | null;
                color: ColorPaletteConfiguration;
            }[],
            xAxisDimensionCode: representationConfig.xAxis.dimensionCode,
            segmentationDimensionCode: representationConfig.segmentation ? representationConfig.segmentation.dimensionCode : null!,
            displayZeros: representationConfig.displayZeros
        }),

        defaultConfig: {
            measures: [],
            xAxis: null!,
            segmentation: null,
            displayZeros: null
        }
    },
    [BusinessIntelligenceRepresentationCode.SCATTER_CHART]: {
        representationCode: BusinessIntelligenceRepresentationCode.SCATTER_CHART,

        configEditorComponent: BusinessIntelligenceScatterChartRepresentationConfigEditorComponent,
        isRepresentationConfigValid: (config, metadata) => {
            if (!config) {
                return false;
            }
            if (!(config.groupBy && config.groupBy.dimensionCode)) {
                return false;
            }

            const groupByDimension = metadata.dimensions.find(dimension => dimension.dimensionCode === config.groupBy.dimensionCode);

            if (continuousFormattableType.includes(groupByDimension!.typeConfig.type)) {
                return false;
            }

            if (!(config.xAxis && config.xAxis.measureCode)) {
                return false;
            }

            if (!(config.yAxis && config.yAxis.measureCode)) {
                return false;
            }

            return true;
        },

        getMeasuresFromRepresentationConfig: config =>
            config.pointSize && config.pointSize.measureCode
                ? [config.xAxis.measureCode, config.yAxis.measureCode, config.pointSize.measureCode].map(measureCode => {
                      return { measureCode };
                  })
                : [config.xAxis.measureCode, config.yAxis.measureCode].map(measureCode => {
                      return { measureCode };
                  }),
        getDimensionsFromRepresentationConfig: config => {
            const dimensions = [config.groupBy];

            if (config.segmentation) {
                dimensions.push(config.segmentation);
            }

            if (config.click) {
                dimensions.push(config.click);
            }

            return dimensions;
        },
        getSortFromRepresentationConfig: () => [],
        getLimitFromRepresentationConfig: () => null,
        getShouldFilterNullResultFromRepresentationConfig: () => null,

        representationConfigToCommonConfig: config =>
            config
                ? {
                      measures: [
                          { measureCode: config.xAxis.measureCode },
                          { measureCode: config.yAxis.measureCode },
                          isNotNullish(config.pointSize) ? { measureCode: config.pointSize.measureCode } : null
                      ].filter(isNotNullish),
                      dimensions: [config.groupBy, config.segmentation].filter(isNotNullish),
                      sortCriteria: [],
                      limit: null
                  }
                : {
                      measures: [],
                      dimensions: [],
                      sortCriteria: [],
                      limit: null
                  },
        commonConfigToRepresentationConfig: commonConfig => ({
            xAxis: commonConfig.measures.length ? { measureCode: commonConfig.measures[0].measureCode } : null!,
            yAxis: commonConfig.measures.length > 1 ? { measureCode: commonConfig.measures[1].measureCode } : null!,
            pointSize: commonConfig.measures.length > 2 ? { measureCode: commonConfig.measures[2].measureCode } : null,
            groupBy: commonConfig.dimensions[0],
            segmentation: commonConfig.dimensions[1] ? commonConfig.dimensions[1] : null,
            click: null
        }),

        representationComponent: BusinessIntelligenceScatterChartComponent,
        representationConfigToComponentConfig: representationConfig => ({
            xAxisMeasureCode: representationConfig.xAxis.measureCode,
            yAxisMeasureCode: representationConfig.yAxis.measureCode,
            pointSizeMeasureCode: representationConfig.pointSize?.measureCode ? representationConfig.pointSize.measureCode : null,
            groupByDimensionCode: representationConfig.groupBy.dimensionCode,
            segmentationDimensionCode: representationConfig.segmentation ? representationConfig.segmentation.dimensionCode : null!,
            clickDimensionCode: representationConfig.click?.dimensionCode ?? null
        }),

        defaultConfig: {
            xAxis: { measureCode: null! },
            yAxis: { measureCode: null! },
            pointSize: { measureCode: null! },
            groupBy: null!,
            segmentation: null,
            click: null
        }
    },
    [BusinessIntelligenceRepresentationCode.SINGLE_VALUE]: {
        representationCode: BusinessIntelligenceRepresentationCode.SINGLE_VALUE,

        configEditorComponent: BusinessIntelligenceSingleValueRepresentationConfigEditorComponent,
        isRepresentationConfigValid: config => {
            if (!config) {
                return false;
            }
            if (!(config.measure && config.measure.measureCode)) {
                return false;
            }
            return true;
        },

        getMeasuresFromRepresentationConfig: config => (config.measure ? [{ measureCode: config.measure.measureCode }] : []),
        getDimensionsFromRepresentationConfig: () => [],
        getSortFromRepresentationConfig: () => [],
        getLimitFromRepresentationConfig: () => null,
        getShouldFilterNullResultFromRepresentationConfig: () => null,

        representationConfigToCommonConfig: config =>
            config
                ? {
                      measures: config.measure && config.measure.measureCode ? [{ measureCode: config.measure.measureCode }] : [],
                      dimensions: [],
                      sortCriteria: [],
                      limit: null
                  }
                : {
                      measures: [],
                      dimensions: [],
                      sortCriteria: [],
                      limit: null
                  },
        commonConfigToRepresentationConfig: commonConfig => ({
            measure: commonConfig.measures.length ? { measureCode: commonConfig.measures[0].measureCode } : null!,
            caption: ''
        }),
        representationComponent: BusinessIntelligenceSingleValueComponent,
        representationConfigToComponentConfig: representationConfig => ({
            measureCode: representationConfig.measure.measureCode,
            caption: representationConfig.caption
        }),
        defaultConfig: {
            measure: { measureCode: null! },
            caption: ''
        }
    },
    [BusinessIntelligenceRepresentationCode.PIVOT_TABLE]: {
        representationCode: BusinessIntelligenceRepresentationCode.PIVOT_TABLE,

        configEditorComponent: BusinessIntelligencePivotTableRepresentationConfigEditorComponent,
        isRepresentationConfigValid: (config, metadata) => {
            if (!config || !config.lines || !config.lines.length || !config.measure || !config.measure.measureCode || !config.pivot) {
                return false;
            }

            const pivotDimension = metadata.dimensions.find(dimension => dimension.dimensionCode === config.pivot.dimensionCode);

            if (
                temporalFormattableType.includes(pivotDimension!.typeConfig.type) &&
                !(config.pivot.groupingParams && config.pivot.groupingParams.granularity)
            ) {
                return false;
            }
            return true;
        },

        getMeasuresFromRepresentationConfig: config => {
            return config.measure ? [config.measure] : [];
        },

        getDimensionsFromRepresentationConfig: config => {
            return [config.pivot, ...config.lines].filter(isNotNullish);
        },
        getSortFromRepresentationConfig: config =>
            config.sortCriteria
                ? [{ memberCode: config.pivot.dimensionCode, sortDirection: SortOrder.ASC }, ...config.sortCriteria]
                : [{ memberCode: config.pivot.dimensionCode, sortDirection: SortOrder.ASC }],
        getLimitFromRepresentationConfig: () => 50_000, // max possible limit for now, and maybe it would be dangerous to go higher x)
        getShouldFilterNullResultFromRepresentationConfig: () => null,

        representationConfigToCommonConfig: config =>
            config
                ? {
                      measures: config.measure ? [{ measureCode: config.measure.measureCode }] : [],
                      dimensions: [config.pivot, ...config.lines].filter(isNotNullish),
                      sortCriteria: config.sortCriteria,
                      limit: null
                  }
                : {
                      measures: [],
                      dimensions: [],
                      sortCriteria: [],
                      limit: null
                  },
        commonConfigToRepresentationConfig: commonConfig => ({
            lines: commonConfig.dimensions
                .slice(1)
                .filter(dimension =>
                    isNotNullish(dimension.groupingParams) && isNotNullish(dimension.groupingParams.granularity) ? false : true
                ),
            pivot: commonConfig.dimensions[0] ? commonConfig.dimensions[0] : null!,
            measure: { measureCode: commonConfig.measures[0] ? commonConfig.measures[0].measureCode : null! },
            sortCriteria: commonConfig.sortCriteria
        }),
        representationComponent: BusinessIntelligencePivotTableComponent,
        representationConfigToComponentConfig: representationConfig => ({
            orderedColumnDimensionCodes: representationConfig.lines.map(dimension => dimension.dimensionCode),
            pivotDimensionCode: representationConfig.pivot.dimensionCode,
            measure: { measureCode: representationConfig.measure.measureCode }
        }),
        defaultConfig: {
            measure: null!,
            lines: [],
            pivot: null!,
            sortCriteria: []
        }
    },
    [BusinessIntelligenceRepresentationCode.TABLE]: {
        representationCode: BusinessIntelligenceRepresentationCode.TABLE,

        configEditorComponent: BusinessIntelligenceTableRepresentationConfigEditorComponent,
        isRepresentationConfigValid: (config, _) => {
            if (!config || !config.members || !config.members.length) {
                return false;
            }

            return true;
        },

        getMeasuresFromRepresentationConfig: config => {
            return config.members
                .map(member => {
                    return isBusinessIntelligenceMeasure(member) ? member.memberConfiguration : null;
                })
                .filter(isNotNullish);
        },
        getDimensionsFromRepresentationConfig: config => {
            return config.members
                .map(member => (isBusinessIntelligenceDimension(member) ? member.memberConfiguration : null))
                .filter(isNotNullish);
        },
        getSortFromRepresentationConfig: config => {
            // If specific sort is registered
            if (config.sortCriteria?.length > 0) return config.sortCriteria;
            const firstMeasure = config.members.find(member => member.type === BusinessIntelligenceMemberType.MEASURE);

            return firstMeasure
                ? [
                      {
                          memberCode: firstMeasure.memberConfiguration.measureCode,
                          sortDirection: SortOrder.DESC
                      } as BusinessIntelligenceSortCriterion
                  ]
                : [];
        },

        getLimitFromRepresentationConfig: config => config.limit ?? null,
        getShouldFilterNullResultFromRepresentationConfig: config => config.displayLinesWithNullValues,

        representationConfigToCommonConfig: config =>
            config
                ? {
                      measures: config.members
                          ? config.members
                                .map(member =>
                                    isBusinessIntelligenceMeasure(member) ? { measureCode: member.memberConfiguration.measureCode } : null
                                )
                                .filter(isNotNullish)
                          : [],
                      dimensions: config.members
                          .map(member => (isBusinessIntelligenceDimension(member) ? member.memberConfiguration : null))
                          .filter(isNotNullish),
                      sortCriteria: config.sortCriteria,
                      limit: config.limit ?? null
                  }
                : {
                      measures: [],
                      dimensions: [],
                      sortCriteria: [],
                      limit: 50
                  },
        commonConfigToRepresentationConfig: commonConfig => ({
            members: [
                ...commonConfig.measures.map(measureCode => {
                    return {
                        type: BusinessIntelligenceMemberType.MEASURE as BusinessIntelligenceMemberType.MEASURE,
                        memberConfiguration: { measureCode: measureCode.measureCode }
                    };
                }),
                ...commonConfig.dimensions.map(dimensionConfiguration => {
                    return {
                        type: BusinessIntelligenceMemberType.DIMENSION as BusinessIntelligenceMemberType.DIMENSION,
                        memberConfiguration: dimensionConfiguration
                    };
                })
            ],
            sortCriteria: commonConfig.sortCriteria,
            limit: commonConfig.limit ?? undefined,
            displayLinesWithNullValues: false
        }),
        representationComponent: BusinessIntelligenceTableComponent,
        representationConfigToComponentConfig: representationConfig => ({
            orderedColumnMemberCodes: representationConfig.members
                .map(member => {
                    if (isBusinessIntelligenceMeasure(member)) {
                        return member.memberConfiguration.measureCode;
                    }
                    if (isBusinessIntelligenceDimension(member)) {
                        return member.memberConfiguration.dimensionCode;
                    }
                    return null;
                })
                .filter(isNotNullish),
            isResultCountLimited: representationConfig.limit !== null
        }),
        defaultConfig: {
            members: [],
            sortCriteria: [],
            limit: undefined,
            displayLinesWithNullValues: false
        }
    }
};

@Injectable({ providedIn: 'root' })
export class BusinessIntelligenceRepresentationsProvider {
    private getRepresentationDescription<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code
    ): RepresentationDescription<Code> {
        return representationDescriptions[representationCode];
    }

    getRepresentationConfigEditorComponent<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code
    ): Type<BusinessIntelligenceRepresentationConfigEditorComponent<BusinessIntelligenceRepresentationCodeConfig<Code>>> | null {
        if (!representationCode) return null;
        return this.getRepresentationDescription(representationCode).configEditorComponent;
    }

    getRepresentationComponent<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code
    ): Type<BusinessIntelligenceRepresentationComponent<BusinessIntelligenceRepresentationCodeComponentConfig<Code>>> | null {
        if (!representationCode) return null;
        return this.getRepresentationDescription(representationCode).representationComponent;
    }

    getMeasureCodesFromRepresentationConfig<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code,
        representationConfig: BusinessIntelligenceRepresentationCodeConfig<Code>
    ): BusinessIntelligenceMeasureConfiguration[] {
        return this.getRepresentationDescription(representationCode).getMeasuresFromRepresentationConfig(representationConfig);
    }

    getDimensionsFromRepresentationConfig<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code,
        representationConfig: BusinessIntelligenceRepresentationCodeConfig<Code>
    ): BusinessIntelligenceDimensionConfiguration[] {
        return this.getRepresentationDescription(representationCode).getDimensionsFromRepresentationConfig(representationConfig);
    }

    getComponentConfigFromRepresentationConfig<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code,
        representationConfig: BusinessIntelligenceRepresentationCodeConfig<Code>
    ): BusinessIntelligenceRepresentationCodeComponentConfig<Code> {
        return this.getRepresentationDescription(representationCode).representationConfigToComponentConfig(representationConfig);
    }

    oldRepresentationConfigToNewRepresentationConfig<
        OldCode extends BusinessIntelligenceRepresentationCode,
        NewCode extends BusinessIntelligenceRepresentationCode
    >(
        oldRepresentationCode: OldCode,
        representationConfig: BusinessIntelligenceRepresentationCodeConfig<OldCode>,
        newRepresentationCode: NewCode,
        metadata: MembersMetaData
    ): BusinessIntelligenceRepresentationCodeConfig<NewCode> {
        const commonConfig =
            this.getRepresentationDescription(oldRepresentationCode).representationConfigToCommonConfig(representationConfig);
        return this.getRepresentationDescription(newRepresentationCode).commonConfigToRepresentationConfig(commonConfig, metadata);
    }

    getDefaultRepresentationConfig<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code
    ): BusinessIntelligenceRepresentationCodeConfig<Code> {
        return this.getRepresentationDescription(representationCode).defaultConfig;
    }

    isRepresentationConfigValid<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code,
        representationConfig: BusinessIntelligenceRepresentationCodeConfig<Code>,
        metadata: MembersMetaData
    ): boolean {
        return this.getRepresentationDescription(representationCode).isRepresentationConfigValid(representationConfig, metadata);
    }

    getSortFromRepresentationConfig<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code,
        representationConfig: BusinessIntelligenceRepresentationCodeConfig<Code>,
        metadata: MembersMetaData
    ): BusinessIntelligenceSortCriterion[] {
        return this.getRepresentationDescription(representationCode).getSortFromRepresentationConfig(representationConfig, metadata);
    }

    getLimitFromRepresentationConfig<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code,
        representationConfig: BusinessIntelligenceRepresentationCodeConfig<Code>
    ): number | null {
        return this.getRepresentationDescription(representationCode).getLimitFromRepresentationConfig(representationConfig);
    }

    getShouldFilterNullResultFromRepresentationConfig<Code extends BusinessIntelligenceRepresentationCode>(
        representationCode: Code,
        representationConfig: BusinessIntelligenceRepresentationCodeConfig<Code>
    ): boolean | null {
        return this.getRepresentationDescription(representationCode).getShouldFilterNullResultFromRepresentationConfig(
            representationConfig
        );
    }
}
